/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import { computed, inject, Injectable } from '@angular/core';
import { MainStore } from '../../store/main-store/main-store';
import { TravelersStore } from '../../core/stores/travelers.store';
import { DestinationsStore } from '../../pages/trip-details/where-are-you-travelling/store/destinations.store';
import { TripDatesStore } from '../../pages/trip-details/when-are-you-travelling/store/trip-dates.store';
import { EProcessType, IExperiment } from '@pc/common';
import { DataLayer } from '../models/data-layer.model';
import { EcommerceService } from './ecommerce.service';
import { EcommerceEvents } from '../models/ecommerce.model';
import { QuoteOverviewService } from './quote/quote-overview.service';
import { ProcessTypeStore } from '../../store/process-type-store/process-type-store';
import { CoverType } from '../models/cover.interface';
import { AnalyticsSteps, AnalyticsStepsOrder, AnalyticsActions } from '../models/analytics.model';
declare const window: {
  VWO: any;
  _vwo_exp: any;
  dataLayer: any;
  fbq: any;
  openCover: (coverId: number) => void;
  OpenContactFormFromSuggestionPopup: (campaignName?: string) => void;
};

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  public experiments: IExperiment[] = [];
  private dataLayer: Partial<DataLayer>[] = window.dataLayer || [];

  private mainStore = inject(MainStore);
  private processTypeStore = inject(ProcessTypeStore);
  private travelersStore = inject(TravelersStore);
  private destinationsStore = inject(DestinationsStore);
  private tripDatesStore = inject(TripDatesStore);
  private ecommerceService = inject(EcommerceService);
  private quoteOverviewService = inject(QuoteOverviewService);

  private tripFirstQuote = 0;
  private lastCompletedStep = AnalyticsSteps.Who;
  private totalPrice = computed(() => this.quoteOverviewService.quotePrice());

  constructor() {
    this.dataLayer = window.dataLayer = window.dataLayer || [];
  }

  public initialAnalyticsProcess(): void {
    this.dataLayer.push({ ecommerce: null });
    this.dataLayer.push({ event: EcommerceEvents.checkout, ecommerce: this.ecommerceService.eCommerceInit() });
  }

  public pushGeneralEvent(event: string, customData?: Record<string, string>): void {
    const processData = this.getProcessData();

    const layer = { event, ...processData, ...customData };
    this.dataLayer.push(layer);
  }

  public initVWOExperiments(): void {
    try {
      window.VWO.push([
        'onEventReceive',
        'vA',
        (data: any): void => {
          const vwoExperiments = window._vwo_exp || {};
          const experimentId = data[1];
          const variationId = data[2];
          const experiment = vwoExperiments[experimentId];

          const vwoExperiment: IExperiment = {
            id: experimentId,
            variationId: variationId,
            variationName: experiment?.comb_n[variationId],
            name: experiment?.name,
            type: experiment?.type
          };

          this.addExperiment(vwoExperiment);
        }
      ]);
    } catch {
      console.warn('VWO is not defined');
    }
  }

  public pushSalesFunnel(
    step: AnalyticsSteps,
    action?: string,
    sendStoreData?: boolean,
    customData?: Record<string, string>
  ): void {
    const storeData = sendStoreData ? this.getStoreData() : {};
    const processData = this.getProcessData();

    const layer = { event: 'sales_funnel', step, action, ...storeData, ...processData, ...customData };
    this.dataLayer.push(layer);

    if (action === AnalyticsActions.Continue) {
      this.lastCompletedStep = this.isStepCompleted(step) ? this.lastCompletedStep : step;
    }
  }

  public pushEcommerceEvent(event: EcommerceEvents, itemId?: number): void {
    if (!event) return;

    const processData = this.getProcessData();
    const items = this.ecommerceService.getEcommerceItems(itemId);
    const value = parseFloat(this.totalPrice().toFixed(2));
    const transactionId = 0;

    const itemsReady: DataLayer = {
      event: event,
      ...processData,
      ecommerce: {
        transaction_id: transactionId,
        currency: 'AUD',
        value: value,
        items: items
      }
    };

    this.dataLayer?.push({ ecommerce: null });
    this.dataLayer?.push(itemsReady);
  }

  private addExperiment(newExperiment: IExperiment): void {
    if (!this.experiments.find((experiment) => experiment.id === newExperiment.id)) {
      this.experiments.push(newExperiment);
    }
  }

  private getStoreData(): Record<string, string | number | undefined> {
    const data = {
      travellers_age: this.travelersStore
        .allTravelers()
        .map((t) => t.age)
        .filter((age) => age !== -1)
        ?.toString(),

      travellers_state: this.travelersStore.adultTravelers()[0]?.state?.text,
      travellers_composition: this.ecommerceService.policyComposition,
      trip_destination: this.destinationsStore
        .pickedDestinationsData()
        .map((d) => d.title)
        .toString(),

      trip_period_duration: this.tripDatesStore.tripLength(),
      trip_first_quote: this.getTripFirstQuote(),
      core_cover_quote: this.getTripBasePrice(),
      optional_cover_quote: this.isStepCompleted(AnalyticsSteps.Mandatory)
        ? this.sumCoverPrice(CoverType.optional)
        : undefined,
      activity_cover_quote: this.isStepCompleted(AnalyticsSteps.Optional)
        ? this.sumCoverPrice(CoverType.activity)
        : undefined,
      health_declaration_quote: this.isStepCompleted(AnalyticsSteps.Health)
        ? this.sumCoverPrice(CoverType.activity)
        : undefined,
      medical_assessment_quote: this.isStepCompleted(AnalyticsSteps.Medical) ? this.totalPrice() : undefined
    };

    return Object.fromEntries(Object.entries(data).filter(([, value]) => value && value !== '0'));
  }

  private isStepCompleted(step: AnalyticsSteps): boolean {
    return (
      AnalyticsStepsOrder[this.lastCompletedStep as keyof typeof AnalyticsStepsOrder] >=
      AnalyticsStepsOrder[step as keyof typeof AnalyticsStepsOrder]
    );
  }

  private getTripFirstQuote(): number {
    if (!this.tripFirstQuote) {
      this.tripFirstQuote = this.getTripBasePrice();
    }

    return this.tripFirstQuote;
  }

  private getTripBasePrice(): number {
    return this.quoteOverviewService.basePrice();
  }

  private sumCoverPrice(targetType: CoverType): number {
    let totalPrice = this.getTripBasePrice();

    for (let type = 0; type <= targetType; type++) {
      totalPrice += this.quoteOverviewService
        .pickedCovers()
        .filter((cover) => cover.type === type && cover.type !== CoverType.core)
        .reduce((sum, cover) => sum + cover.price, 0);
    }

    return totalPrice;
  }

  private getProcessData(): Record<string, string> {
    return {
      isLoggedIn: false.toString(),
      traffic_type: this.getTrafficType(),
      flow_type: this.getFlowType(),
      plan: this.getPlan()
    };
  }

  private getFlowType(): string {
    return this.processTypeStore.source();
  }

  private getPlan(): string {
    return 'Comprehensive';
  }

  private getTrafficType(): string {
    switch (this.mainStore.processType()) {
      case EProcessType.AFFILIATE:
        return 'Affiliate';
      case EProcessType.AGENTS:
        return 'Broker';
      default:
        return 'Direct';
    }
  }
}
